import { useMemo, useSyncExternalStore } from "react";
import { InsightsAgentService } from "../InsightsAgentService.ts";
import { Research, ResearchId } from "../AppState.ts";
import { useInsightsAgentService } from "./useInsightsAgentService.tsx";

const useAppState = <T>(
  appService: InsightsAgentService,
  selector: () => T,
): T => useSyncExternalStore(appService.subscribe, selector);

const emptyArray: never[] = [];
const emptyObject: Record<string, never> = {};
export const useAuthState = () => {
  const appService = useInsightsAgentService();
  return useAppState(appService, () => appService.getState().authState);
};

export const useResearchesState = () => {
  const appService = useInsightsAgentService();
  return useAppState(appService, () => appService.getState().researchMap);
};

export const useResearchState = (researchId: ResearchId): Research | null => {
  const appService = useInsightsAgentService();
  return useAppState(
    appService,
    () => appService.getState().researchMap.items[researchId] || null,
  );
};

export const useMessagesState = (researchId: ResearchId) => {
  const appService = useInsightsAgentService();
  // If we don't memoize this, the value will be re-created on every render
  const emptyArray = useMemo(() => [], []);
  return useAppState(
    appService,
    () => appService.getState().messages.items[researchId] || emptyArray,
  );
};

export const useReferencesState = (researchId: ResearchId) => {
  const appService = useInsightsAgentService();
  // If we don't memoize this, the value will be re-created on every render
  const emptyArray = useMemo(() => [], []);
  return useAppState(
    appService,
    () => appService.getState().fileReferences.items[researchId] || emptyArray,
  );
};

export const useFiltersState = (researchId: ResearchId) => {
  const appService = useInsightsAgentService();

  return useAppState(
    appService,
    () => appService.getState().filters.items[researchId] || emptyArray,
  );
};

export const useFiltersLoadingState = () => {
  const appService = useInsightsAgentService();

  return useAppState(appService, () => appService.getState().filters.loading);
};

export const useFilterOptionsState = () => {
  const appService = useInsightsAgentService();

  return useAppState(
    appService,
    () => appService.getState().filterOptions.items || emptyObject,
  );
};

export const useFullFilterOptionsState = () => {
  const appService = useInsightsAgentService();

  return useAppState(appService, () => appService.getState().filterOptions);
};

export const useKnowledgeSources = () => {
  const appService = useInsightsAgentService();
  return useAppState(
    appService,
    () => appService.getState().knowledgeSources.items,
  );
};

export const useKnowledgeSourcesLoading = () => {
  const appService = useInsightsAgentService();
  return useAppState(
    appService,
    () => appService.getState().knowledgeSources.loading,
  );
};

export const useResearchTypes = () => {
  const appService = useInsightsAgentService();
  return useAppState(appService, () => appService.getState().researchTypes);
};

export const useTermsAndConditionsState = () => {
  const appService = useInsightsAgentService();
  return useAppState(
    appService,
    () => appService.getState().termsAndConditions,
  );
};
